import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import LineTitle from '../atoms/titles/LineTitle';
import AirlineTab from '../molecules/AirlineTab';
import PlanePlanTable from '../molecules/PlanePlanTable';
import {
  setSelectedOutboundFlight,
  setSelectedInboundFlight,
  setSelectedOutboundDate,
  setSelectedInboundDate,
} from '../../modules/flight';
import { AIRLINES } from '../../constants';
import NoResults from '../molecules/NoResults';

const flightSelector = (state) => state.flight;

const PlanePlan = ({
  handleSelectFlightOutbound, handleSelectFlightInbound, hideAirlineTab, handleFlightSearch,
}) => {
  const {
    outboundFlights,
    inboundFlights,
    selectedInboundFlight,
    selectedOutboundFlight,
    selectedOutboundDate,
    selectedInboundDate,
    searchedFlightsInput,
    fetchingOutbound,
    fetchingInbound,
    unavailableCarrierSelected,
    searchTime,
  } = useSelector(flightSelector);
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const outboundDepartureAirportName = (outboundFlights && outboundFlights.length) ?
    outboundFlights[0].departureAirportName : '';
  const outboundArrivalAirportName = (outboundFlights && outboundFlights.length) ?
    outboundFlights[0].arrivalAirportName : '';
  const inboundDepartureAirportName = (inboundFlights && inboundFlights.length) ?
    inboundFlights[0].departureAirportName : '';
  const inboundArrivalAirportName = (inboundFlights && inboundFlights.length) ?
    inboundFlights[0].arrivalAirportName : '';

  if (unavailableCarrierSelected) {
    return (
      <div className="planePlanContent">
        <NoResults
          title="ご迷惑をおかけしております"
          desc="該当の路線が無いか、アクセスが集中して繋がりにくくなっております。<br/>条件を変えていただくか、しばらくして再検索していただけますようお願い申し上げます。"
        />
      </div>
    );
  }

  if (!searchedFlightsInput || fetchingOutbound || fetchingInbound) {
    return null;
  }

  const {
    airline: searchedAirline, outbound: searchedOutbound, inbound: searchedInbound,
  } = searchedFlightsInput;

  const showFlights = !!(outboundFlights && outboundFlights.length && inboundFlights && inboundFlights.length);

  const buttonText = isOpen ? '閉じる' : '全ての航空便を見る';

  const containerStyle = showFlights ? {
    marginTop: hideAirlineTab ? 30 : undefined,
  } : null;

  const showSearchTime = searchTime.outbound && searchTime.inbound;
  const oldestSearchTime = showSearchTime ? (searchTime.inbound > searchTime.outbound ? searchTime.outbound : searchTime.inbound) : '';
  const formattedSearchTime = oldestSearchTime ? moment(String(oldestSearchTime), "YYYYMMDDHHmm").format("YYYY年MM月DD日HH時mm分") : '';

  return (
    <div className="planePlanContainer" style={containerStyle}>
      {!hideAirlineTab && (
        <AirlineTab handleFlightSearch={handleFlightSearch} />
      )}
      {showFlights ? (
        <div className="planePlanContent">
          {showSearchTime ? (<div className="planePlanUpdated">更新日時：{formattedSearchTime}</div>) : ''}
          <div className="planePlan">
            <div className="planePlanList">
              <div className="planePlanListHeader">
                <LineTitle text="行き" />
                <span className="flight">{outboundDepartureAirportName}→{outboundArrivalAirportName}</span>
              </div>
              <div className="planePlanTableWrapper">
                <div className={`planePlanTable ${isOpen ? 'expand' : ''}`}>
                  <PlanePlanTable
                    tableId="outbound"
                    flightOptions={outboundFlights}
                    selectedFlight={selectedOutboundFlight}
                    isSelectedDateShowing={searchedOutbound.date === selectedOutboundDate}
                    handleSelectFlight={(flight) => {
                      if (handleSelectFlightOutbound) {
                        handleSelectFlightOutbound(flight);
                        return;
                      }

                      dispatch(setSelectedOutboundFlight(flight));
                      dispatch(setSelectedOutboundDate(searchedOutbound.date));
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="planePlanList">
              <div className="planePlanListHeader">
                <LineTitle text="帰り" />
                <span className="flight">{inboundDepartureAirportName}→{inboundArrivalAirportName}</span>
              </div>
              <div className="planePlanTableWrapper">
                <div className={`planePlanTable ${isOpen ? 'expand' : ''}`}>
                  <PlanePlanTable
                    tableId="inbound"
                    flightOptions={inboundFlights}
                    selectedFlight={selectedInboundFlight}
                    isSelectedDateShowing={searchedInbound.date === selectedInboundDate}
                    handleSelectFlight={(flight) => {
                      if (handleSelectFlightInbound) {
                        handleSelectFlightInbound(flight);
                        return;
                      }

                      dispatch(setSelectedInboundFlight(flight));
                      dispatch(setSelectedInboundDate(searchedInbound.date));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="planePlanTableButton" onClick={() => setIsOpen(!isOpen)}>
            <div className={`planePlanTableButtonText ${isOpen ? 'expand' : ''}`}>{buttonText}</div>
          </div>
          {searchedAirline === AIRLINES.ana ? (
            <p className="note">
              ※プレミアムクラスは選択できません。<br />
              ※ANA便名のうち4700～4840番台の便はエア・ドゥ、2400番台の便はソラシドエア、3800番台の便はスターフライヤー、3100番台の便はＩＢＥＸエアラインズ、4641～4699番台の便はオリエンタルエアブリッジの機材・乗員による運航となります。<br />
              【運航航空会社】ANA：全日本空輸、ADO:エア・ドゥ、SNA：ソラシド エア、SFJ：スターフライヤー
            </p>
          ) : (
            <p className="note">
              【乗継便選択時のクラスＪ席ご注意】<br />
              ・経由便の場合、全区間でクラスＪ席を予約できない場合があります。<br />
              ・クラスＪ設定機材で空席がある区間のみ、クラスＪ席でのご予約となります。<br />
              ・クラスＪ利用時の差額は、ご予約可能な区間分のみ適用し表示しております。<br /><br />
              【運航航空会社】JAL/JL：日本航空、JTA：日本トランスオーシャン航空、RAC：琉球エアコミューター、JAC：日本エアコミューター、J-AIR：ジェイ・エア、FDA：フジドリームエアラインズ、HAC：北海道エアシステム、AMX：天草エアライン
            </p>
          )}
        </div>
      ) : (
        <div className="planePlanContent">
          <NoResults
            title="ご迷惑をおかけしております"
            desc="該当の路線が無いか、アクセスが集中して繋がりにくくなっております。<br/>条件を変えていただくか、しばらくして再検索していただけますようお願い申し上げます。"
          />
        </div>
      )}
    </div>
  );
};

PlanePlan.propTypes = {
  handleSelectFlightOutbound: PropTypes.func,
  handleSelectFlightInbound: PropTypes.func,
  hideAirlineTab: PropTypes.bool,
  handleFlightSearch: PropTypes.func.isRequired,
};

export default PlanePlan;
